import React from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Column, Row, Spinner, Text} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {ShowMessageBox} from "react-vmessagebox";
import {RunInAction_Set} from "web-vcore";
import {ToJSON_Compact} from "../../../../Utils/General/Serialization.js";
import {DialogStyle} from "../../../../Utils/UI/GlobalStyles.js";
import {objPlacementsStore} from "./ObjPlacements.js";

/** Shows the object-placements editor in a moveable message-box dialog (live-updates the scene as entries are changed). */
export function ShowObjPlacementsDialog() {
	ShowMessageBox({
		title: "Object placements",
		cancelButton: true,
		cancelButtonProps: {text: "Close"},
		okButton: false,

		// don't use overlay/background-blocker (lets the user interact with the scene/map behind the dialog, and move the dialog freely for a better view)
		overlayStyle: {background: "none", pointerEvents: "none"},
		containerStyle: {pointerEvents: "auto"},

		message: ()=><ObjPlacementsDialogUI/>,
	});
}

const ObjPlacementsDialogUI = observer_mgl(()=>{
	// tree-view over the registry of objects loaded through useSimple3DComp; placements-map read/written directly (deeply observable)
	const models = objPlacementsStore.models;

	return (
		<Column style={DialogStyle({width: 1000, height: 700})}>
			<Row>
				<Text style={{fontWeight: "bold"}}>Placements:</Text>
				<Button ml={5} text="Copy JSON" onClick={()=>navigator.clipboard.writeText(ToJSON_Compact(objPlacementsStore.map))}/>
			</Row>
			<ScrollView mt={5} style={{flex: 1}} contentStyle={{
				width: "100%",
				minWidth: null, // clear out the "minWidth: fit-content" default
			}}>
				<Column style={{padding: 5}}>
					{Object.keys(models).length == 0 && <Text style={{opacity: .6, fontStyle: "italic"}}>No models loaded yet. (objects loaded via useSimple3DComp show up here)</Text>}
					{Object.entries(models).map(([fileKey, model])=>(
						<Column key={fileKey} mt={5}>
							<PlacementRow placementKey={fileKey} label={fileKey} bold/>
							{Object.entries(model.objects).map(([subPath, obj])=>(
								<PlacementRow key={subPath} placementKey={`${fileKey}/${subPath}`} obj={obj}/>
							))}
						</Column>
					))}
				</Column>
			</ScrollView>
			<Row mt={5} center>
				<Text style={{color: "rgba(160,160,160,1)"}}>Check a field to start overriding it (seeds from the object's current values). Uncheck/press Delete to fall back to defaults. Use "Copy JSON" to paste the result over objPlacementsDefaults in ObjPlacements.ts.</Text>
			</Row>
		</Column>
	);
});

/** One row per registered object (or the file-level row, which has no source-object); shows per-field override checkbox + spinners when enabled. */
const PlacementRow = observer_mgl((props: {placementKey: string, obj?: {position: {x: number, y: number, z: number}, rotation: {x: number, y: number, z: number}, scale: {x: number, y: number, z: number}}, label?: string, bold?: boolean})=>{
	const {placementKey, obj, label, bold} = props;
	const map = objPlacementsStore.map;
	// show an implicit empty entry for objects without a map-entry (displaying as "no fields overridden")
	const placement = map[placementKey] ?? {};

	const EnsureEntry = ()=> {
		// an entry gets created (if absent) as soon as any field is enabled
		map[placementKey] ??= {};
		return map[placementKey];
	};
	const EnableField = (field: "position"|"rotation"|"scale")=>RunInAction_Set(()=>{
		const entry = EnsureEntry();
		// seed the field with the object's current values (or neutral defaults for the file-level row, which has no single source-object)
		const vals = obj ? ([obj[field].x, obj[field].y, obj[field].z]) : (field == "scale" ? [1, 1, 1] : [0, 0, 0]);
		entry[field] = [vals[0], vals[1], vals[2]];
	});
	const DisableField = (field: "position"|"rotation"|"scale")=>RunInAction_Set(()=>{
		const entry = map[placementKey];
		if (entry == null) return;
		entry[field] = undefined;
		// if no fields are overridden anymore, delete the entry entirely (so no "override" remains)
		if (entry.position == null && entry.rotation == null && entry.scale == null) delete map[placementKey];
	});

	return (
		<Row sel mt={2} style={{paddingLeft: 20, flexWrap: "wrap"}}>
			<Text style={{minWidth: bold ? 250 : undefined, fontWeight: bold ? "bold" : undefined, opacity: bold ? .9 : undefined}}>{label ?? (placementKey.split("/").slice(1).join("/") || "<whole model>")}</Text>
			{(["position", "rotation", "scale"] as const).map(field=>(
				<Row key={field}>
					<CheckBox ml={10} text={field} value={placement[field] != null} onChange={val=>val ? EnableField(field) : DisableField(field)}/>
					{placement[field] != null && [0, 1, 2].map(i=>(
						<Spinner key={i} ml={2}
							min={field == "scale" ? 0 : -Infinity}
							step={field == "scale" ? .05 : "any"}
							value={placement[field]![i]}
							onChange={val=>RunInAction_Set(()=> {
								const entry = EnsureEntry();
								// fallback to the object's current values for any component not yet stored in the entry (neutral for the file-level row)
								const defaultValue = obj ? [obj[field].x, obj[field].y, obj[field].z][i] : (field == "scale" ? 1 : 0);
								const cur = entry[field];
								entry[field] = [cur?.[0] ?? defaultValue, cur?.[1] ?? defaultValue, cur?.[2] ?? defaultValue];
								entry[field][i] = val;
							})}/>
					))}
				</Row>
			))}
			{placement.position != null || placement.rotation != null || (placement.scale != null)
				? <Button ml={10} text="Delete" onClick={()=>RunInAction_Set(()=>delete map[placementKey])}/>
				: null}
		</Row>
	);
});
